/**
 * Light in the dark (M3-05).
 *
 * A light source is a small record in world state with a **radius** and a
 * **deadline**, and that is the whole model. Deliberately not a property of the
 * actor carrying it: M3-06 wants a campfire burning down on a tile nobody is
 * standing on, and a "torch" boolean on a combatant would have to be unpicked
 * the day it does.
 *
 * The deadline is a tick it goes out on, not a fuel counter decremented every
 * tick — the third time this codebase makes that call (see `ecology.ts`,
 * `wound.ts`, `flora.ts`), and for the same three reasons: it survives a
 * restart that skipped an hour, it costs nothing on the ticks where nothing
 * happens, and a replay stepping it twice cannot make a torch burn shorter.
 *
 * What this buys, in one sentence: **provisioning matters**. A brand lasts a
 * fraction of a night, so going up the moor after dark is a thing you either
 * prepared for in daylight or regret.
 *
 * State and pure helpers only — the rules that light one, move it and put it
 * out live in `sky.ts`, for the reason `mind.ts` is split from `beasts.ts`:
 * `world.ts` has to name `Lights` in order to store it.
 */
import { chebyshev, DIRECTION_VECTORS } from './actor.js';
import { tileAt, tileDefAt } from './map.js';
import { hasLineOfSight } from './senses.js';
import { TICKS_PER_GAME_HOUR } from './time.js';
/**
 * What kind of burning thing this is.
 *
 * A brand (M3-05) is carried, short and cannot be fed; a fire (M3-06) stands on
 * a tile, can be fed to keep it going, cooks, warms, and can get away from you.
 * One record with a kind rather than two tables, because everything that reads a
 * light — the sight radius, the culling, the renderer's glow — asks the same
 * three questions of both, and the day M4 lights a rushlight in a hut it should
 * not need a third table.
 */
export const LIGHT_KINDS = ['brand', 'fire'];
/**
 * What a brand is worth: eight tiles, against a night sight of three.
 *
 * The gap is the mechanic. Three tiles is not enough to see a wolf decide; eight
 * is enough to fight in and not enough to feel safe, which is exactly the
 * reading M3-05 asks for.
 */
export const TORCH_RADIUS = 8;
/**
 * How long a brand burns: three game hours, or seven and a half real minutes.
 *
 * Night is about five game hours of full dark, so one brand does not see you
 * through it. That is the number doing the work — a second bundle of firewood
 * in the satchel is the difference between crossing the moor and sitting it out.
 */
export const TORCH_TICKS = TICKS_PER_GAME_HOUR * 3;
/** What foraged firewood turns into when you strike a light (M3-03's `fuel`). */
export const FUEL_TAG = 'fuel';
/**
 * A campfire: five tiles of light and warmth, for four game hours a bundle.
 *
 * Smaller than a brand's eight on purpose. A fire is not a better torch — it is
 * a *place*, and the thing it is worth is the ring you can cook, rest and be
 * found in. Making it out-light a torch as well would leave the torch with
 * nothing to be for.
 */
export const FIRE_RADIUS = 5;
/** A banked village hearth lights a room as strongly as a campfire. */
export const HEARTH_RADIUS = FIRE_RADIUS;
const CARDINALS = ['s', 'e', 'w', 'n'];
/**
 * Infer which way an authored hearth faces from the room around it.
 *
 * Hearths are terrain rather than entities, so old saves carry no facing
 * field. Looking for the longest passable run gives the authored village the
 * intended answer (toward its doors and living space) and upgrades those saves
 * without a migration. South wins a genuine tie because the village glyphs
 * are drawn against their north walls.
 */
export function hearthFacingAt(map, x, y) {
    let best = 's';
    let bestScore = -1;
    for (const direction of CARDINALS) {
        const [dx, dy] = DIRECTION_VECTORS[direction];
        let score = 0;
        for (let distance = 1; distance <= 4; distance += 1) {
            const def = tileDefAt(map, x + dx * distance, y + dy * distance);
            if (!def.passable)
                break;
            score += 1;
            if (tileAt(map, x + dx * distance, y + dy * distance) === 'door')
                score += 2;
        }
        if (score > bestScore) {
            best = direction;
            bestScore = score;
        }
    }
    return best;
}
/** Whether a tile lies in the broad room-facing beam of a hearth. */
export function inHearthBeam(facing, hearthX, hearthY, x, y) {
    if (x === hearthX && y === hearthY)
        return true;
    const [vx, vy] = DIRECTION_VECTORS[facing];
    const dx = x - hearthX;
    const dy = y - hearthY;
    const forward = dx * vx + dy * vy;
    const sideways = Math.abs(dx * vy - dy * vx);
    return forward >= 0 && sideways <= forward + 1;
}
/** Remaining hearth-light radius at one tile, including walls and direction. */
export function hearthLightAt(map, x, y) {
    let best = 0;
    for (let hy = y - HEARTH_RADIUS; hy <= y + HEARTH_RADIUS; hy += 1) {
        for (let hx = x - HEARTH_RADIUS; hx <= x + HEARTH_RADIUS; hx += 1) {
            if (tileAt(map, hx, hy) !== 'hearth')
                continue;
            const distance = chebyshev(hx, hy, x, y);
            if (distance >= HEARTH_RADIUS)
                continue;
            const facing = hearthFacingAt(map, hx, hy);
            if (!inHearthBeam(facing, hx, hy, x, y))
                continue;
            if (!hasLineOfSight(map, hx, hy, x, y))
                continue;
            best = Math.max(best, HEARTH_RADIUS - distance);
        }
    }
    return best;
}
/** What one bundle of firewood buys a standing fire. */
export const FIRE_TICKS = TICKS_PER_GAME_HOUR * 4;
/**
 * The longest a fire can be banked up in advance.
 *
 * A cap rather than an unbounded stack, so that a satchel of firewood cannot be
 * turned into a fire that burns for a week unattended. Feeding is meant to be
 * something somebody does through the night, which is the small reason to still
 * be awake at a camp.
 */
export const FIRE_MAX_TICKS = TICKS_PER_GAME_HOUR * 12;
/**
 * The last stretch, when it is embers: light and warmth halved.
 *
 * A fire that went from full to nothing on one tick would give a player no
 * reason to look at it until it was too late. Embers are the warning, and they
 * are the same warning a real fire gives.
 */
export const EMBER_TICKS = TICKS_PER_GAME_HOUR;
export function createLight(id, x, y, tick, options = {}) {
    const kind = options.kind ?? 'brand';
    return {
        id,
        kind,
        x,
        y,
        radius: options.radius ?? (kind === 'fire' ? FIRE_RADIUS : TORCH_RADIUS),
        since: tick,
        outAt: tick + (options.burnTicks ?? (kind === 'fire' ? FIRE_TICKS : TORCH_TICKS)),
        carriedBy: options.carriedBy ?? null,
        onTinder: options.onTinder ?? false,
    };
}
/**
 * How little room has to be left before feeding a fire is refused.
 *
 * The ceiling is measured *from now*, so it creeps forward a tick at a time as
 * the world runs — which would make an exact `outAt >= ceiling` test almost
 * unreachable and the refusal that depends on it dead code. An hour of headroom
 * is the honest threshold: below that, a bundle of firewood buys so little that
 * saying so is better than quietly swallowing it.
 */
export const FIRE_FEED_FLOOR = TICKS_PER_GAME_HOUR;
/**
 * Put more wood on. Returns whether there was any worthwhile room for it.
 *
 * Capped at `FIRE_MAX_TICKS` from now rather than added blindly, so a satchel
 * of firewood cannot be turned into a fire that burns for a week unattended.
 */
export function feedFire(light, tick, ticks = FIRE_TICKS) {
    const ceiling = tick + FIRE_MAX_TICKS;
    if (light.outAt >= ceiling - FIRE_FEED_FLOOR)
        return false;
    light.outAt = Math.min(ceiling, light.outAt + ticks);
    return true;
}
/** Down to embers: still warm, half the light, and not long left. */
export function isEmbers(light, tick) {
    return isBurning(light, tick) && light.outAt - tick <= EMBER_TICKS;
}
/** The radius a light is actually throwing right now, embers included. */
export function radiusOf(light, tick) {
    if (!isBurning(light, tick))
        return 0;
    return isEmbers(light, tick) ? Math.max(1, Math.round(light.radius / 2)) : light.radius;
}
export function isBurning(light, tick) {
    return tick < light.outAt;
}
/**
 * How close a light is to going out, 0–1. The client flickers on it, and the
 * log warns on it — a torch that died without a word would be a torch the
 * player blames the game for.
 */
export function burnedDown(light, tick) {
    const life = light.outAt - light.since;
    if (life <= 0)
        return 1;
    return Math.min(1, Math.max(0, (tick - light.since) / life));
}
/** The brightest light this actor is holding, in tiles. 0 if their hands are empty. */
export function carriedRadius(lights, actorId, tick) {
    let best = 0;
    for (const light of Object.values(lights)) {
        if (light.carriedBy !== actorId)
            continue;
        const radius = radiusOf(light, tick);
        if (radius > best)
            best = radius;
    }
    return best;
}
/**
 * How well lit a tile is, in tiles of radius to spare — 0 for dark.
 *
 * Used for what *others* can see of you: standing in the ring of a campfire is
 * standing where anyone within its light can pick you out, which is the cost of
 * the fire and the reason M3-06's camp is a decision.
 */
export function litAt(lights, x, y, tick) {
    let best = 0;
    for (const light of Object.values(lights)) {
        const radius = radiusOf(light, tick);
        if (radius <= 0)
            continue;
        const distance = chebyshev(light.x, light.y, x, y);
        if (distance > radius)
            continue;
        const spare = radius - distance;
        if (spare > best)
            best = spare;
    }
    return best;
}
//# sourceMappingURL=light.js.map